import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Check, ShieldX, ArrowLeft } from "lucide-react";
import { LogoWordmark } from "../components/Logo";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token) setError("Link non valido o incompleto.");
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("La password deve essere di almeno 8 caratteri.");
            return;
        }
        if (password !== confirm) {
            setError("Le due password non coincidono.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setSuccess(true);
            toast.success("Password aggiornata, puoi accedere");
            setTimeout(() => navigate("/login"), 2000);
        } catch (err) {
            setError(err.response?.data?.detail || "Errore durante il reset");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col p-6 sm:p-10 lg:p-16 grid-bg bg-white">
            <LogoWordmark />
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-md fade-up" data-testid="reset-password-page">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0C4650] hover:text-[#091f26] mb-4"
                    >
                        <ArrowLeft className="w-4 h-4" /> Torna al login
                    </Link>

                    {success ? (
                        <div data-testid="reset-success">
                            <div className="w-16 h-16 rounded-full bg-[#C7FF2C] flex items-center justify-center">
                                <Check className="w-8 h-8 text-[#0C4650]" strokeWidth={3} />
                            </div>
                            <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold text-[#0C4650]">
                                Password aggiornata!
                            </h1>
                            <p className="mt-3 text-gray-600">
                                Stai per essere reindirizzato alla pagina di login…
                            </p>
                        </div>
                    ) : !token ? (
                        <div data-testid="reset-no-token">
                            <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center">
                                <ShieldX className="w-8 h-8 text-rose-600" strokeWidth={2.5} />
                            </div>
                            <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold text-[#0C4650]">
                                Link non valido
                            </h1>
                            <p className="mt-3 text-gray-600">
                                Il link sembra incompleto. Richiedi un nuovo link di reset.
                            </p>
                            <Link
                                to="/forgot-password"
                                className="mt-6 btn-primary w-full inline-flex"
                            >
                                Richiedi nuovo link
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0C4650] leading-tight">
                                Imposta una nuova password
                            </h1>
                            <p className="mt-3 text-gray-600">
                                Scegli una password di almeno 8 caratteri.
                            </p>
                            <form onSubmit={submit} className="mt-6 space-y-4">
                                <div>
                                    <label className="label">Nuova password</label>
                                    <input
                                        type="password"
                                        className="input"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={8}
                                        autoFocus
                                        placeholder="••••••••"
                                        data-testid="reset-password-input"
                                    />
                                </div>
                                <div>
                                    <label className="label">Ripeti nuova password</label>
                                    <input
                                        type="password"
                                        className="input"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        minLength={8}
                                        placeholder="••••••••"
                                        data-testid="reset-password-confirm"
                                    />
                                </div>
                                {error && (
                                    <div
                                        className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 text-sm"
                                        data-testid="reset-error"
                                    >
                                        {error}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full"
                                    data-testid="reset-submit"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Lock className="w-4 h-4" />
                                    )}
                                    Aggiorna la password
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
            <div className="text-xs text-gray-400 mt-8">
                © {new Date().getFullYear()} Scintilla Hub — Accendiamo connessioni.
            </div>
        </div>
    );
}
